import Api from "@/api"
import { showErrorMessage } from "@/composition/useLoading"
import { type DocumentUpload } from "@/models"
import { useIncidentHandler } from "@/pages/shared/incident-form/IncidentLogHandler"
import { useUser } from "@/stores/coreDataStore"
import { getFileIcon } from "@/utils/fileIcons"
import { faDownload, faEye } from "@fortawesome/free-solid-svg-icons"
import { css } from "vite-css-in-js"
import { computed, ref, toRef } from "vue"
import { defineComponent, formatFileSize, inferProps, proxyProp, type ReactiveComponent } from "vue-utils"
import Icon from "../Icon"
import BootstrapButton from "../form/BootstrapButton"

interface Props {
	document: DocumentUpload
}

const rowStyles = css`
	td {
		padding: 0.45rem 0.5rem;

		display: flex;
		column-gap: 0.25rem;
		align-items: center;

		font-size: 1rem;

		svg {
			color: #333;
		}
	}
	td:first-child {
		column-gap: 0.5rem;
		svg {
			font-size: 1.25rem;
		}
		span {
			white-space: nowrap;
			text-overflow: ellipsis;
			overflow: hidden;
		}
	}
	td.actions {
		svg {
			color: white;
		}
	}
`

const ExistingDocumentsRow: ReactiveComponent<Props> = (props) => {
	const handler = useIncidentHandler()

	const document = proxyProp(props, "document")
	const uploadedBy = useUser(document.uploadedBy.userId)
	const uploadedDate = toRef(() => document.uploadedBy.timeStamp)

	const downloading = ref(false)
	const previewing = ref(false)

	async function download(preview: boolean) {
		if (handler.isNewIncident) {
			return
		}

		const downloadRef = preview ? previewing : downloading
		if (downloadRef.value) {
			return
		}
		downloadRef.value = true
		try {
			let downloadLink = await Api.downloads.generateLink(props.document.guid)
			if (preview) {
				downloadLink += "?preview=true"
			}
			if (window.location.host === "localhost:3000") {
				downloadLink = `http://localhost:5171${downloadLink}`
			}
			window.open(downloadLink, "_blank", "noopener noreferrer")
		} catch (e) {
			void showErrorMessage("Failed to download file", (e as Error).message)
		} finally {
			downloadRef.value = false
		}
	}

	const dateFormatted = computed(() => {
		const time = uploadedDate.value
		if (!time) {
			return handler.readOnly ? "-" : ""
		}
		return handler.formatTime(time)
	})

	return () => (
		<tr key={document.guid} class={rowStyles}>
			<td>
				<Icon icon={getFileIcon(document.mimeType)} />
				<span title={document.fileName}>{document.fileName}</span>
			</td>
			<td title={handler.isNewIncident ? "Date Modified" : "Date Uploaded"}>
				<span>{dateFormatted.value}</span>
			</td>
			<td title="Uploaded By">
				<span>{uploadedBy.value ? uploadedBy.value.fullName : "Unknown"}</span>
			</td>
			<td>{formatFileSize(document.fileSize)}</td>
			{!handler.isNewIncident && (
				<td class="actions">
					<BootstrapButton
						color="primary"
						title="Preview File"
						isSubmitting={previewing.value}
						disabled={previewing.value}
						onClick={() => void download(true)}
					>
						{!previewing.value && <Icon icon={faEye} />}
					</BootstrapButton>
					<BootstrapButton
						color="success"
						title="Download File"
						isSubmitting={downloading.value}
						disabled={downloading.value}
						onClick={() => void download(false)}
					>
						{!downloading.value && <Icon icon={faDownload} />}
					</BootstrapButton>
				</td>
			)}
		</tr>
	)
}

export default defineComponent(ExistingDocumentsRow, inferProps<Props>())
